import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { useBandejaTramites, useTiposTramite } from '../../hooks/useBandejaTramites';
import { useEmpresasActivas } from '../../hooks/useUsuarios';
import { filtrosFromParams, filtrosToParams, type BandejaFiltros } from '../../types/tramiteGremio';
import PanelFiltros from './PanelFiltros';
import BandejaLista from './BandejaLista';
import TramiteGremioDetalle from './TramiteGremioDetalle';

/**
 * Buzón de trámites del gremio: filtros y lista a la izquierda, detalle y acciones a la derecha.
 * En pantallas chicas se ve una cosa por vez (la lista, o el detalle con «Volver»).
 * Los filtros viajan en la URL (se pueden compartir) y el trámite elegido en la ruta /tramites/:id.
 */
export default function BandejaTramites() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();

  const filtros = useMemo(() => filtrosFromParams(params), [params]);
  const { data, isLoading, isFetching, error } = useBandejaTramites(filtros);
  const { data: tipos = [] } = useTiposTramite();
  const { data: empresas = [] } = useEmpresasActivas();

  const cambiar = useCallback(
    (cambios: Partial<BandejaFiltros>) => {
      setParams(filtrosToParams({ ...filtros, ...cambios }), { replace: true });
    },
    [filtros, setParams],
  );

  const irAlTramite = (tid: string) => navigate({ pathname: `/tramites/${tid}`, search });
  const volver = () => navigate({ pathname: '/tramites', search });

  const mostrarLista = desktop || !id;
  const mostrarDetalle = desktop || !!id;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Trámites de afiliados
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Los trámites que inician los afiliados desde el portal.
      </Typography>

      <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: desktop ? 'minmax(340px, 2fr) 3fr' : '1fr' }}>
        {mostrarLista && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <PanelFiltros filtros={filtros} onChange={cambiar} conteos={data?.conteos ?? {}} tipos={tipos} empresas={empresas} />
            </Paper>
            <BandejaLista
              filas={data?.filas ?? []}
              total={data?.total ?? 0}
              pagina={filtros.pagina}
              seleccionado={id}
              cargando={isLoading || isFetching}
              error={error as Error | null}
              onSeleccionar={irAlTramite}
              onPagina={(p) => cambiar({ pagina: p })}
            />
          </Stack>
        )}
        {mostrarDetalle && (
          <Box sx={{ minWidth: 0 }}>
            {id ? (
              <TramiteGremioDetalle key={id} id={id} onVolver={desktop ? undefined : volver} />
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <InboxIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography>Elegí un trámite de la lista para ver el detalle y tratarlo.</Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
